import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  calculateFormulaValue,
  listCellDisplayValue,
  normaliseInteractionType,
  normaliseListColumns,
  normaliseListRows,
} from '../utils/assetGrids';

const normaliseUrl = (value = '') => {
  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) return '';
  if (/^https?:\/\//i.test(trimmedValue)) return trimmedValue;
  return `https://${trimmedValue}`;
};

const formatFormulaValue = (value, column = {}) => {
  if (value === '') return '';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;
  return column.formula?.preset === 'percentage' ? `${numericValue.toFixed(2)}%` : numericValue.toFixed(2);
};

const interactionDraftKey = (rowId, columnId) => `${rowId}::${columnId}`;

const hasDraftValue = (value) => (
  value !== undefined &&
  value !== null &&
  value !== ''
);

function ListCellValue({ column, row }) {
  const value = column.type === 'formula'
    ? calculateFormulaValue(row, column)
    : row.cells?.[column.id];

  if (column.type === 'image') {
    const imageUrl = value?.downloadUrl || value?.url || value || '';
    return imageUrl ? <img className="share-list-cell-image" src={imageUrl} alt={value?.name || column.label} /> : null;
  }

  if (column.type === 'file') {
    const fileUrl = value?.downloadUrl || value?.url || '';
    return fileUrl ? (
      <a href={fileUrl} rel="noreferrer" target="_blank">
        {value?.name || 'Open file'}
      </a>
    ) : listCellDisplayValue(value, column.type, column.currencySymbol);
  }

  if (column.type === 'link') {
    const linkUrl = normaliseUrl(value);
    return linkUrl ? (
      <a href={linkUrl} rel="noreferrer" target="_blank">
        {listCellDisplayValue(value, column.type, column.currencySymbol) || linkUrl}
      </a>
    ) : null;
  }

  if (column.type === 'formula') {
    return formatFormulaValue(value, column);
  }

  return listCellDisplayValue(value, column.type, column.currencySymbol);
}

function InteractionCell({ column, disabled, onClear, onDraftChange, value }) {
  const interaction = column.interaction || {};
  const type = normaliseInteractionType(interaction.type);
  const options = Array.isArray(interaction.options) ? interaction.options : [];

  if (type === 'buttons') {
    return (
      <div className="flex flex-wrap gap-2">
        {(options.length ? options : ['Yes', 'No']).map((option) => {
          const isSelected = value === option;
          return (
            <button
              aria-pressed={isSelected}
              className={`rounded border px-3 py-2 text-xs font-semibold transition ${
                isSelected
                  ? 'border-slate-950 bg-slate-950 text-white'
                  : 'border-slate-300 bg-white text-slate-700 hover:border-slate-950 hover:text-slate-950'
              }`}
              disabled={disabled}
              key={option}
              onClick={() => (isSelected ? onClear() : onDraftChange(option))}
              type="button"
            >
              {option}
            </button>
          );
        })}
      </div>
    );
  }

  if (type === 'select') {
    return (
      <select
        className="field min-w-40"
        disabled={disabled}
        onChange={(event) => (event.target.value ? onDraftChange(event.target.value) : onClear())}
        value={value || ''}
      >
        <option value="">Select</option>
        {options.map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    );
  }

  if (type === 'check') {
    return (
      <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
        <input
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(event) => (event.target.checked ? onDraftChange(true) : onClear())}
          type="checkbox"
        />
        Check
      </label>
    );
  }

  if (type === 'rating') {
    return (
      <div className="flex gap-1" aria-label={`${column.label} rating`}>
        {[1, 2, 3, 4, 5].map((rating) => (
          <button
            aria-label={`${rating} star${rating === 1 ? '' : 's'}`}
            className={`text-xl leading-none ${Number(value) >= rating ? 'text-amber-400' : 'text-slate-300'} transition hover:text-amber-400`}
            disabled={disabled}
            key={rating}
            onClick={() => (Number(value) === rating ? onClear() : onDraftChange(rating))}
            type="button"
          >
            {'\u2605'}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="grid min-w-48 gap-1">
      <input
        className="field"
        disabled={disabled}
        onChange={(event) => (event.target.value ? onDraftChange(event.target.value) : onClear())}
        placeholder="Type response"
        value={value || ''}
      />
    </div>
  );
}

function ShareListTable({ grid, link, onInteraction, searchQuery = '' }) {
  const [interactionDrafts, setInteractionDrafts] = useState({});
  const [interactionSubmitStatus, setInteractionSubmitStatus] = useState('');
  const [isSubmittingInteractions, setIsSubmittingInteractions] = useState(false);
  const columns = normaliseListColumns(grid?.listColumns);
  const rows = normaliseListRows(grid?.listRows, columns);
  const normalisedSearchQuery = searchQuery.trim().toLowerCase();
  const visibleRows = normalisedSearchQuery
    ? rows.filter((row) => JSON.stringify(row).toLowerCase().includes(normalisedSearchQuery))
    : rows;
  const actionUrl = normaliseUrl(grid?.listActionUrl);
  const hasRowMedia = rows.some((row) => row.imageUrl || row.tags?.length || row.actionLabel || row.actionUrl);
  const interactionColumns = columns.filter((column) => column.type === 'interaction');
  const canSubmitInteractions = Boolean(onInteraction && link?.id && interactionColumns.length);
  const interactionDraftEntries = Object.entries(interactionDrafts).filter(([, draft]) => hasDraftValue(draft?.value));

  const setInteractionDraft = (row, column, value) => {
    const key = interactionDraftKey(row.id, column.id);
    setInteractionSubmitStatus('');
    setInteractionDrafts((currentDrafts) => ({
      ...currentDrafts,
      [key]: { row, column, value },
    }));
  };

  const clearInteractionDraft = (rowId, columnId) => {
    const key = interactionDraftKey(rowId, columnId);
    setInteractionSubmitStatus('');
    setInteractionDrafts((currentDrafts) => {
      const { [key]: removedDraft, ...nextDrafts } = currentDrafts;
      return nextDrafts;
    });
  };

  const handleSubmitInteractions = async () => {
    if (!canSubmitInteractions || !interactionDraftEntries.length) return;
    setIsSubmittingInteractions(true);
    setInteractionSubmitStatus('');
    try {
      await onInteraction({
        link,
        grid,
        interactions: interactionDraftEntries.map(([, draft]) => ({
          row: draft.row,
          column: draft.column,
          value: draft.value,
        })),
      });
      setInteractionDrafts({});
      setInteractionSubmitStatus('Submitted');
    } catch (error) {
      setInteractionSubmitStatus(error.message || 'Unable to submit interactions.');
    } finally {
      setIsSubmittingInteractions(false);
    }
  };

  return (
    <section className="share-list-renderer">
      <div className="share-list-header">
        {grid?.listImageUrl && (
          <img
            alt={grid.listTitle || grid.title || 'List'}
            className="share-list-header-image"
            src={grid.listImageUrl}
          />
        )}
        <div className="min-w-0">
          <p className="label-caps">{visibleRows.length} row{visibleRows.length === 1 ? '' : 's'}</p>
          <h2>{grid?.listTitle || grid?.title || 'List'}</h2>
          {grid?.listDescription && (
            <div className="asset-description mt-2 text-sm text-slate-600">
              <ReactMarkdown>{grid.listDescription}</ReactMarkdown>
            </div>
          )}
          {grid?.listTags?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {grid.listTags.map((tag) => <span className="chip" key={tag}>{tag}</span>)}
            </div>
          )}
        </div>
        {(actionUrl && grid?.listActionLabel) || canSubmitInteractions ? (
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            {actionUrl && grid?.listActionLabel && (
              <a className="share-list-action" href={actionUrl} rel="noreferrer" target="_blank">
                {grid.listActionLabel}
              </a>
            )}
            {canSubmitInteractions && (
              <button
                className="rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                disabled={!interactionDraftEntries.length || isSubmittingInteractions}
                onClick={handleSubmitInteractions}
                type="button"
              >
                {isSubmittingInteractions ? 'Submitting...' : `Submit${interactionDraftEntries.length ? ` (${interactionDraftEntries.length})` : ''}`}
              </button>
            )}
            {interactionSubmitStatus && (
              <span className={`w-full text-right text-xs font-semibold ${interactionSubmitStatus === 'Submitted' ? 'text-emerald-700' : 'text-red-600'}`}>
                {interactionSubmitStatus}
              </span>
            )}
          </div>
        ) : null}
      </div>

      <div className="share-list-table-shell">
        <table className="share-list-table">
          <thead>
            <tr>
              {hasRowMedia && <th aria-label="Row media" />}
              {columns.map((column) => <th key={column.id}>{column.label}</th>)}
              {hasRowMedia && <th aria-label="Row actions" />}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, rowIndex) => {
              const rowActionUrl = normaliseUrl(row.actionUrl);
              return (
                <tr key={row.id}>
                  {hasRowMedia && (
                    <td className="share-list-media-cell">
                      {row.imageUrl ? (
                        <img src={row.imageUrl} alt={`Row ${rowIndex + 1}`} />
                      ) : null}
                    </td>
                  )}
                  {columns.map((column) => {
                    const draftKey = interactionDraftKey(row.id, column.id);
                    return (
                      <td key={column.id}>
                        {column.type === 'interaction' ? (
                          <InteractionCell
                            column={column}
                            disabled={!canSubmitInteractions || isSubmittingInteractions}
                            onClear={() => clearInteractionDraft(row.id, column.id)}
                            onDraftChange={(value) => setInteractionDraft(row, column, value)}
                            value={interactionDrafts[draftKey]?.value}
                          />
                        ) : (
                          <ListCellValue column={column} row={row} />
                        )}
                      </td>
                    );
                  })}
                  {hasRowMedia && (
                    <td>
                      <div className="share-list-row-actions">
                        {row.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {row.tags.map((tag) => <span className="chip text-[0.65rem]" key={tag}>{tag}</span>)}
                          </div>
                        )}
                        {rowActionUrl && row.actionLabel && (
                          <a href={rowActionUrl} rel="noreferrer" target="_blank">
                            {row.actionLabel}
                          </a>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {!visibleRows.length && (
              <tr>
                <td colSpan={columns.length + (hasRowMedia ? 2 : 0)}>
                  {normalisedSearchQuery ? 'No matching rows.' : 'No rows yet.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default ShareListTable;
